/**
 * Persistent Storage Service
 * Handles storage with fallback mechanisms for environments where localStorage doesn't persist
 */

import logger from '../helpers/logger';

const STORAGE_TEST_KEY = '_storage_persistence_test';
const STORAGE_AVAILABLE_KEY = '_storage_available';

class PersistentStorageService {
  constructor() {
    this.storageAvailable = this.checkStorageAvailability();
    this.persistenceWarningShown = false;
  }

  /**
   * Check if localStorage is available and working
   */
  checkStorageAvailability() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      logger.error('localStorage not available:', e);
      return false;
    }
  }

  /**
   * Test if localStorage persists across sessions
   * Sets a test value and checks if it exists on next load
   */
  testPersistence() {
    try {
      const testValue = localStorage.getItem(STORAGE_TEST_KEY);
      const now = Date.now();

      if (testValue) {
        const lastTest = parseInt(testValue, 10);
        // If test value exists and is older than 5 seconds, persistence works
        if (now - lastTest > 5000) {
          logger.log('✅ localStorage persistence verified');
          localStorage.setItem(STORAGE_AVAILABLE_KEY, 'true');
          return true;
        }
      }

      // Set test value for next session
      localStorage.setItem(STORAGE_TEST_KEY, now.toString());
      
      // Check if we've detected issues before
      const wasAvailable = localStorage.getItem(STORAGE_AVAILABLE_KEY);
      if (wasAvailable === 'false') {
        logger.warn('⚠️ localStorage persistence issues detected previously');
        return false;
      }

      return null; // Unknown yet
    } catch (e) {
      logger.error('Error testing localStorage persistence:', e);
      return false;
    }
  }

  /**
   * Mark that localStorage doesn't persist
   */
  markPersistenceFailure() {
    try {
      localStorage.setItem(STORAGE_AVAILABLE_KEY, 'false');
      sessionStorage.setItem(STORAGE_AVAILABLE_KEY, 'false');
    } catch (e) {
      // Ignore
    }
  }

  /**
   * Get item with fallback to cookies
   */
  getItem(key) {
    try {
      // Try localStorage first
      const localValue = localStorage.getItem(key);
      if (localValue !== null) {
        return localValue;
      }

      // Fallback to cookies
      return this.getCookie(key);
    } catch (e) {
      logger.error(`Error getting item ${key}:`, e);
      return null;
    }
  }

  /**
   * Set item with fallback to cookies
   */
  setItem(key, value) {
    let localSuccess = false;
    let cookieSuccess = false;

    try {
      // Try localStorage
      localStorage.setItem(key, value);
      localSuccess = true;
    } catch (e) {
      logger.warn(`Failed to set localStorage item ${key}:`, e);
    }

    try {
      // Also set in cookies as backup (expires in 1 year)
      this.setCookie(key, value, 365);
      cookieSuccess = true;
    } catch (e) {
      logger.warn(`Failed to set cookie ${key}:`, e);
    }

    if (!localSuccess && !cookieSuccess) {
      throw new Error(`Failed to persist ${key} in any storage mechanism`);
    }

    return localSuccess || cookieSuccess;
  }

  /**
   * Remove item from all storage locations
   */
  removeItem(key) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Ignore
    }

    try {
      this.deleteCookie(key);
    } catch (e) {
      // Ignore
    }
  }

  /**
   * Get cookie value
   */
  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    return null;
  }

  /**
   * Set cookie value
   */
  setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/; SameSite=Strict';
  }

  /**
   * Delete cookie
   */
  deleteCookie(name) {
    document.cookie = name + '=; Max-Age=-99999999; path=/';
  }

  /**
   * Check if we should show persistence warning
   */
  shouldShowPersistenceWarning() {
    if (this.persistenceWarningShown) {
      return false;
    }

    const persistenceStatus = this.testPersistence();
    
    // Show warning if persistence explicitly failed
    if (persistenceStatus === false) {
      this.persistenceWarningShown = true;
      return true;
    }

    return false;
  }
}

const persistentStorageService = new PersistentStorageService();

export default persistentStorageService;

